import { getIdentifiers, getValueFromRow, sortRows } from '../../data/helpers';
import { DataStore } from '../../data/store';
import { GenericError } from '../../design/error/genericError';
import {
  ColumnType,
  DataColumn,
  DataRow,
  DataRows,
  IdentifierColumn,
  KpiWithChangeColumn,
  Params,
  SortingInfo,
} from '../../types';

export type LeaderColumn = {
  column: DataColumn;
  subLabels?: (string | string[])[];
  currency: string;
};

export type LeaderboardInfo = {
  rowIds: string[];
  columns: LeaderColumn[];
  totalsData?: DataRows;
  rowsData: DataRows;
  defaultSortingInfo?: SortingInfo;
};

export type GroupLeaderboardInfo = LeaderboardInfo;

export type GroupInfo = { name: string; data: DataRows; groupSummary: DataRow; groupModelIds: string[] };

export function makeLeaderboardData(
  data: DataStore,
  options: {
    rowIdField: string;
    searchTerm?: string;
    visibleColumns: string[];
  },
  sortingInfo?: SortingInfo
): LeaderboardInfo {
  const { rowIdField, searchTerm, visibleColumns } = options;
  let columns = data.getDataColumns();
  if (visibleColumns.length !== 0) {
    columns = columns.concat(data.getAvailableColumns());
  }

  const nameColumn = columns.find(col => col.name === rowIdField) as IdentifierColumn;
  const columnsToInclude: string[] = columns
    .filter(col => {
      if (col.name === rowIdField) {
        return false;
      }

      if (col.isFixed) {
        return true;
      }

      if (visibleColumns && visibleColumns.length) {
        return visibleColumns.includes(col.name);
      }

      return true;
    })
    .map(col => col.name);

  if (nameColumn.name === 'item_id') {
    columnsToInclude.sort((a, b) => {
      if (a === 'brand_name') return -1;
      if (b === 'brand_name') return 1;
      return 0;
    });
  }

  const dataColumns: DataColumn[] = [];
  columnsToInclude.forEach(name => {
    const col = columns.find(col => col.name === name);
    if (col) {
      dataColumns.push(col);
    }
  });

  const defaultSortColumn = columns.find(column => column.sortOptions?.some(sortOption => sortOption.isDefault));

  const defaultSortOption = defaultSortColumn?.sortOptions?.find(sortOption => sortOption.isDefault);

  if (!defaultSortOption) {
    throw new GenericError('Default sorting not found');
  }

  const defaultSorting: SortingInfo = {
    columnName: defaultSortColumn.name,
    label: defaultSortOption.label,
    direction: defaultSortOption.defaultDirection,
    sortKey: defaultSortOption.sortKey,
    sortType: defaultSortOption.sortType,
    sortIndex: dataColumns[0]?.arrayLength - 1,
  };

  const currency = data.getCurrency();
  const totalsData = data.getGroupedData([])[0];
  const rowsDataSrc = data.getGroupedData([rowIdField])[0];
  const rowsData: DataRows = [];

  const maxMap: Record<string, number[]> = {};
  const kpiCols = dataColumns.filter(col => col.type === ColumnType.Kpi || col.type === ColumnType.KpiWithChange);
  const rowIds: string[] = [];

  rowsDataSrc?.data.forEach(row => {
    kpiCols.forEach(col => {
      const values = row.values[col.name] as (null | number)[];
      const highestValue = Math.max(...(values || [0]));
      if (!maxMap[col.name]) {
        maxMap[col.name] = [];
      }
      maxMap[col.name].push(highestValue);
    });
    let isMatch = searchTerm ? false : true;
    if (searchTerm) {
      const { name } = getIdentifiers(nameColumn, row);
      const terms = searchTerm.split(' ');
      isMatch = terms.every(term => name.toLocaleLowerCase().includes(term.toLocaleLowerCase()));
    }
    if (isMatch) {
      rowIds.push(String(row.values[rowIdField]));
      rowsData.push(row);
    }
  });

  const leaderColumns: LeaderColumn[] = [
    {
      column: nameColumn,
      currency,
    },
    ...dataColumns.map(col => ({
      column: col,
      subLabels: col.arrayLength !== null ? rowsDataSrc?.timeFrame.periods.map(p => p.name) : undefined,
      currency,
    })),
  ];
  const leaderBoardData = {
    rowIds,
    rowsData,
    columns: leaderColumns,
    totalsData: totalsData?.data,
    defaultSortingInfo: defaultSorting,
  };
  return sortLeaderboard(leaderBoardData, sortingInfo);
}

export function sortLeaderboard(leaderBoardData: LeaderboardInfo, sorting: SortingInfo) {
  if (!sorting || !leaderBoardData?.rowIds || !sorting.sortKey) {
    return leaderBoardData;
  }

  const indexedData = leaderBoardData.rowIds.map((rowId, index) => ({
    rowId,
    rowData: leaderBoardData.rowsData[index],
  }));

  const sortKeys = sorting.sortKey.split('|');
  const hasSortKeyData = (row: (typeof indexedData)[0]) => {
    if (sortKeys.length > 1) {
      // handling for composite sort key
      // composite key -> only for model name column
      return true;
    }
    return getValueFromRow(row.rowData, sorting?.sortKey, sorting?.sortIndex) !== null;
  };

  const sortedData = indexedData.sort((a, b) => {
    // primary on sortGravity
    if (a.rowData.values.sortGravity < b.rowData.values.sortGravity) return -1;
    if (a.rowData.values.sortGravity > b.rowData.values.sortGravity) return 1;
    // secondary on null values
    const isDataInA = hasSortKeyData(a);
    const isDataInB = hasSortKeyData(b);
    if (isDataInA && !isDataInB) return -1;
    if (!isDataInA && isDataInB) return 1;
    // tertiary on actual data
    return sortRows(a.rowData, b.rowData, sorting);
  });

  leaderBoardData.rowIds = sortedData.map(row => row.rowId);
  leaderBoardData.rowsData = sortedData.map(row => row.rowData);

  return leaderBoardData;
}

export function getSortKeysFromColumn(col: LeaderColumn): string[] {
  if (!col.column) {
    return [];
  }
  let sortKeysFromColumn: string[] = [col.column.name];

  switch (col.column.type) {
    case ColumnType.Identifier: {
      const properColumn = col.column as IdentifierColumn;
      properColumn?.identifier && sortKeysFromColumn.push(properColumn?.identifier?.join('|'));
      break;
    }
    case ColumnType.KpiWithChange: {
      const properColumn = col.column as KpiWithChangeColumn;
      properColumn?.changeName && sortKeysFromColumn.push(properColumn?.changeName);
      break;
    }
  }

  return sortKeysFromColumn;
}

export const createDataParamForQuickLook = (paramsForData: Params, groupsParam: string) => {
  //delete groups param
  const { [groupsParam]: _, ...cloneDataParams } = paramsForData;
  return cloneDataParams;
};
